// Pure helpers behind the tier ladder's price line and step line. Kept free of
// React and i18n so the copy rules are unit-testable; the tile turns the
// returned shape into translated strings.

export interface PriceStepTier {
  /** Lowest offer for the tier, already reduced by the "discount counts only when
   *  positive" rule; null when no seller lists a price. */
  lowestPrice: number | null
}

export interface PriceStepBelow extends PriceStepTier {
  /** Display ordinal of the lower tier ("Advanced"), used in the copy. */
  name: string
}

export type PriceStep =
  /** The lowest tier in the ladder — nothing to step over. */
  | { kind: 'base' }
  /** No step line: this tier or the one below has no price. */
  | { kind: 'none' }
  /** `+$800 over Advanced` */
  | { kind: 'more'; amount: number; tier: string }
  /** `Same price as Advanced` */
  | { kind: 'same'; tier: string }
  /** `$1,000 less than Standard` — a higher tier that is cheaper at its lowest seller. */
  | { kind: 'less'; amount: number; tier: string }

/**
 * The step copy for a tier given the tier below it in display order (`null`
 * for the base tier). The amount is always non-negative: a cheaper higher
 * tier reports how much less, never a negative step.
 */
export const stepFor = (tier: PriceStepTier, below: PriceStepBelow | null): PriceStep => {
  if (tier.lowestPrice == null) {
    return { kind: 'none' }
  }
  if (below === null) {
    return { kind: 'base' }
  }
  if (below.lowestPrice == null) {
    return { kind: 'none' }
  }
  const difference = tier.lowestPrice - below.lowestPrice
  if (difference > 0) {
    return { kind: 'more', amount: difference, tier: below.name }
  }
  if (difference < 0) {
    return { kind: 'less', amount: -difference, tier: below.name }
  }
  return { kind: 'same', tier: below.name }
}

/**
 * The entry a tier steps over: the next lower entry in display order that has a
 * price, falling back to the immediate neighbour (so an unpriced neighbour still
 * yields "no step line" rather than skipping to a priced tier further down while
 * the immediate one is a mystery). `null` when the tier is last.
 */
export const tierBelow = <T extends PriceStepBelow>(levels: readonly T[], index: number): T | null => {
  if (index >= levels.length - 1) {
    return null
  }
  return levels.slice(index + 1).find((level) => level.lowestPrice != null) ?? levels[index + 1]
}

/** Offers billed monthly ("monthly", "monthly_pair"…) price per pair, per month. */
export const isMonthlyPriceType = (priceType: string | null | undefined): boolean =>
  priceType != null && /monthly/.test(priceType)

/** A tier's price line reads `from $2,998` only when more than one seller lists it. */
export const showsFrom = (sellerCount: number): boolean => sellerCount > 1
